'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { isSafeReturnPath } from '@/lib/auth/return-path';
import { useRouter } from 'next/navigation';
import { cn } from '@/lib/utils';
import {
  Gauge,
  AtSign,
  Reply,
  Bell,
  Check,
  X,
  ShieldCheck,
  ArrowLeft,
  BellOff,
  Terminal,
  FileText,
  ChevronRight,
  Users,
  Rocket,
} from 'lucide-react';
import { ChatIcon } from '@/components/icons/chat-icon';
import { useChatSidebar } from '@/components/chat-sidebar-provider';
import type { Notification } from '@/lib/notifications/service';
import { useT } from '@/components/i18n-provider';
import { Tip } from '@/components/ui/tooltip';
import { useAnalytics } from '@/hooks/use-analytics';
import { BackmixDialog } from '@/components/workspace/backmix-dialog';
import { buildAuditView } from '@/lib/notifications/audit-detail';
import { formatCredits } from '@/lib/balance/units';

/** Picker button labels. Delegates to the one formatter in
 *  `lib/balance/units.ts`: amounts are shown in credits, never dollars, and
 *  no component keeps its own money formatter. */
const fmtMoney = formatCredits;

function ChatIconNoEyes({ className }: { className?: string }) {
  return <ChatIcon className={className} eyes={false} />;
}

const typeConfig: Record<string, { icon: React.ComponentType<{ className?: string }>; color: string }> = {
  // A limit, not money (the e2e visual pass of 2026-10-06, XL-1b): this row
  // is the daily spending limit's approval, and its body is a credits
  // figure; a dollar glyph beside a credits figure is the money shape the
  // credits rule forbids (CLAUDE.md, "Credits copy").
  payment_required: { icon: Gauge, color: 'text-amber-500' },
  message_summary: { icon: ChatIconNoEyes, color: 'text-blue-500' },
  mention: { icon: AtSign, color: 'text-purple-500' },
  reply: { icon: Reply, color: 'text-green-500' },
  follow: { icon: Bell, color: 'text-pink-500' },
  system: { icon: Bell, color: 'text-muted-foreground' },
  content_shared: { icon: Users, color: 'text-sky-500' },
  tool_approval: { icon: ShieldCheck, color: 'text-amber-500' },
  widget_permission_request: { icon: ShieldCheck, color: 'text-amber-500' },
  // An agent asking to publish an app. Rocket is the icon the workspace
  // Publish dialog already uses for publishing, so the bell row and the
  // dialog read as the same action (2026-09-06).
  widget_publish_request: { icon: Rocket, color: 'text-amber-500' },
  connected_account_needs_reauth: { icon: ShieldCheck, color: 'text-amber-500' },
  // Agent/widget audit rows. Post-de-noise these are only the rejected /
  // errored commands (successes stay in the live activity ring), so they
  // read as "an agent action was blocked or failed" — worth a glance.
  'widget.agent_command': { icon: Terminal, color: 'text-muted-foreground' },
  'widget.file_write': { icon: FileText, color: 'text-muted-foreground' },
};

interface NotificationItemProps {
  notification: Notification;
  onMarkRead?: (id: string) => void;
  /**
   * Action dispatcher. `threshold` (dollars) is the auto-approve cap
   * picked via the inline "Set limit" picker (2x / 10x / Custom) —
   * only consumed when `action === 'always_approve'`. Mirrors
   * `useNotifications.performAction` and `CriticalToast.onAction`.
   */
  onAction?: (
    id: string,
    action: 'approve' | 'deny' | 'always_approve' | 'approve_once',
    duration?: string,
    threshold?: number,
  ) => Promise<any>;
  onCtaAction?: (id: string, action: string) => void;
  onDelete?: (id: string) => void;
  onMute?: (sourceType: string, sourceId: string) => void;
  compact?: boolean;
}

export function NotificationItem({
  notification,
  onMarkRead,
  onAction,
  onCtaAction,
  onDelete,
  onMute,
  compact,
}: NotificationItemProps) {
  const t = useT();
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [backmixOpen, setBackmixOpen] = useState(false);
  // Audit rows (agent/widget commands) reveal their structured detail
  // on click instead of cramming op + args into the body.
  const [expanded, setExpanded] = useState(false);
  // Inline "Set limit" picker state — same three-step state machine
  // as CriticalToast / ChatPaymentBanner so the notification
  // dropdown row reads as the same control across surfaces.
  //   'actions' → [Approve] [Set limit] [Deny]
  //   'quick'   → [←] [2x · $X] [10x · $Y] [Custom…]
  //   'custom'  → [←] $[input] [Apply]
  const [picker, setPicker] = useState<'actions' | 'quick' | 'custom'>(
    'actions',
  );
  const [customStr, setCustomStr] = useState('');
  const customInputRef = useRef<HTMLInputElement | null>(null);
  const chatSidebar = useChatSidebar();
  const router = useRouter();
  const { trackPaymentApprove, trackPaymentDeny } = useAnalytics();

  useEffect(() => {
    if (picker === 'custom') {
      requestAnimationFrame(() => customInputRef.current?.focus());
    }
  }, [picker]);

  const config = typeConfig[notification.type] ?? typeConfig.system;
  const Icon = config.icon;
  const data = notification.data as Record<string, any>;
  const isPayment = notification.type === 'payment_required';
  const isToolApproval = notification.type === 'tool_approval';
  const isPermissionRequest = notification.type === 'widget_permission_request';
  const isPublishRequest = notification.type === 'widget_publish_request';
  const permissionFeature =
    typeof data?.feature === 'string' ? (data.feature as string) : undefined;
  // Agent-control requests carry `agentName` instead of `feature` — same
  // notification type, same buttons, so the label just reads whichever is set.
  const permissionAgentName =
    typeof data?.agentName === 'string' && data.agentName
      ? (data.agentName as string)
      : undefined;
  const hasPermissionAction = isPermissionRequest && !notification.actionTaken;
  const hasPublishAction = isPublishRequest && !notification.actionTaken;
  // A publish approval is DECIDED from this row, and its body is the whole
  // safety mechanism. Measured on the rendered card 2026-09-07: body
  // scrollHeight 96 against clientHeight 32 (the two-line clamp), title
  // scrollWidth 545 against clientWidth 332. What the clamp cut was the third
  // sentence, "Unpublishing later takes it out of the catalog but does not
  // close the link, so anyone who already has it keeps access" — the one fact
  // that makes this decision hard to walk back. The person read
  // "...It stays public as you keep editing i…" and pressed Publish, and the
  // truncated title chopped the app name mid-word so they could not even be
  // sure WHICH app they were publishing.
  //
  // So this one type opts out of both the clamp and the title truncate rather
  // than the warning being shortened to fit. It is four sentences in a panel
  // that already scrolls. Every other type keeps the clamp: payment, tool and
  // permission prompts are short, recur, and read as a list.
  const showFullText = isPublishRequest;
  const isAudit =
    notification.type === 'widget.agent_command' ||
    notification.type === 'widget.file_write';
  const hasAction = isPayment && !notification.actionTaken;
  const hasToolApprovalAction = isToolApproval && !notification.actionTaken;
  const timeAgo = getTimeAgo(notification.createdAt, t);

  // Humanize audit rows at RENDER time so nothing is lost: the stored
  // body keeps the (bounded) raw args, and this recovers a clean
  // one-liner + structured key/value detail from it. Works for legacy
  // `op {json}` rows too. See lib/notifications/audit-detail.ts.
  // A BLOCKED AGENT IS SAID IN ONE PLAIN LINE (the e2e visual pass of
  // 2026-10-06, RV-B2): the row's body was the command and up to 400
  // characters of its JSON arguments, which `buildAuditView` turned into
  // "set_mode: ..." for a person. A permission refusal is the one a person
  // can act on, and the request to allow it is its own notification, so
  // the line says that. Rows written before the audit route's fix included;
  // the command and its arguments stay in the expanded detail.
  const isBlockedAgent =
    notification.type === 'widget.agent_command' &&
    data?.ok === false &&
    (data?.errorCode ?? 'permission') === 'permission';
  // THE DETAIL OF A NEW BLOCKED ROW PARSES `data.args` (the lane R review of
  // the same pass, 2026-10-06). Since the fix the audit route stores a fixed
  // English sentence as the body and the bridge's `op {json}` summary in
  // `data.args`. `buildAuditView` shows `data.args` as it is, so "Details"
  // drew a raw JSON blob (its own contract says never), and it parsed the
  // sentence as a command: "Nothing" with "was changed. ..." as its text.
  // So a row that carries `args` (null when the bridge sent no summary)
  // hands that summary to the view as the body it already knows how to
  // parse, and never the sentence. Older rows keep their `op {json}` body.
  const blockedArgs =
    isBlockedAgent && typeof data === 'object' && data !== null && 'args' in data;
  const auditView = isAudit
    ? buildAuditView({
        body: blockedArgs
          ? typeof data.args === 'string'
            ? data.args
            : null
          : notification.body,
        data: blockedArgs ? { ...data, args: undefined } : data,
        createdAt: notification.createdAt,
      })
    : null;
  const bodyText = isBlockedAgent
    ? t('notifications.agentBlockedBody')
    : auditView
      ? auditView.summary
      : notification.body;

  // 2x / 10x base: prefer the SPEND LIMIT that was hit (e.g. $5 cap
  // → $10 / $50) over the trivial per-request amount. Falls back to
  // the request amount when the notification carries no limit.
  const amountStr = data?.amount as string | number | undefined;
  const dailyLimitStr = data?.dailyLimit as string | undefined;
  const amountNum = (() => {
    const n =
      typeof amountStr === 'number'
        ? amountStr
        : typeof amountStr === 'string'
          ? parseFloat(amountStr)
          : NaN;
    return Number.isFinite(n) && n > 0 ? n : 0;
  })();
  const dailyLimitNum = (() => {
    const n = typeof dailyLimitStr === 'string' ? parseFloat(dailyLimitStr) : NaN;
    return Number.isFinite(n) && n > 0 ? n : 0;
  })();
  const pickerBase = dailyLimitNum > 0 ? dailyLimitNum : amountNum;
  const quick2x = pickerBase * 2;
  const quick10x = pickerBase * 10;

  const handleToolApproval = async (action: 'approve' | 'reject') => {
    setActionLoading(action);
    try {
      const res = await fetch(`/api/notifications/${notification.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      if (res.ok) {
        onCtaAction?.(notification.id, action);
      }
    } finally {
      setActionLoading(null);
    }
  };

  // Per-widget feature grant (camera / microphone / …). Same PATCH the
  // CriticalToast uses — approve persists the feature onto the item and
  // reloads that one iframe; deny just marks read.
  const handlePermissionAction = async (action: 'approve' | 'deny') => {
    setActionLoading(action);
    try {
      const res = await fetch(`/api/notifications/${notification.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      if (res.ok) {
        onCtaAction?.(notification.id, action);
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleAction = async (
    action: 'approve' | 'deny' | 'always_approve' | 'approve_once',
    threshold?: number,
  ) => {
    if (!onAction) return;
    setActionLoading(action);
    try {
      // No duration — we set permanent overrides now (the previous
      // 10min/1hr/24hr/forever menu was confusing and rarely used).
      // The threshold is the meaningful axis: how much per request,
      // not how long until the auto-approval expires.
      await onAction(notification.id, action, undefined, threshold);
      const agentId = (data?.agentId ?? data?.agentUsername ?? '') as string;
      if (action === 'deny') {
        trackPaymentDeny(agentId);
      } else {
        trackPaymentApprove(agentId, (data?.amount as number) ?? 0);
      }
    } finally {
      setActionLoading(null);
      setPicker('actions');
    }
  };

  const chatId = data?.chatId as string | undefined;
  const hasChatLink = (isPayment || notification.type === 'message_summary') && chatId;
  const isBackmix = data?.kind === 'backmix';
  const href =
    notification.type === 'mention' && data?.kind === 'comment' && data?.workspaceId
      // Canvas comment mention → open the workspace + deep-link the thread.
      ? `/workspace/${data.workspaceId}${data?.threadId ? `?comment=${data.threadId}` : ''}`
      : (notification.type === 'mention' || notification.type === 'reply') && data?.postId
        // `/c/<slug>/<postId>` never resolved: /c/[...slug] joins the whole
        // path into a channel slug, so the post id made the lookup miss (and
        // channelSlug was never set on these notifications anyway).
        ? `/p/${data.postId}`
        // A share notification opens the shared file itself.
        : notification.type === 'content_shared' && data?.contentId
          ? `/w/${data.contentId}`
          // An app's notice (`notifyUser`) opens where the PLATFORM said it
          // should, a same-origin path only (2026-09-19: a place offered in
          // Revent has to be claimed there, and the notice linked nowhere).
          : notification.type === 'system'
              && (data?.kind === 'app' || data?.kind === 'inbox_request')
              && isSafeReturnPath(data?.appLink)
            // An app's own agent's request opens the APP on it too
            // (2026-09-24, lib/inbox/notify.ts): Revent runs full screen and
            // its owner is there, not in the agent's settings.
            ? (data.appLink as string)
            : undefined;

  const handleClick = () => {
    if (!notification.isRead) onMarkRead?.(notification.id);
    if (hasChatLink) {
      chatSidebar.open(chatId);
    } else if (isBackmix) {
      setBackmixOpen(true);
    } else if (isAudit && auditView && auditView.fields.length > 0) {
      setExpanded((v) => !v);
    }
  };

  const isExpandable = !!auditView && auditView.fields.length > 0;
  // A row that acts on click must SAY so: without a role it exposed nothing
  // to the accessibility tree (screen readers and automation saw an inert
  // div). href rows get semantics from the wrapping <Link>; the rest are
  // buttons, keyboard-activatable, named by their visible content.
  const rowActs = !href && (hasChatLink || isBackmix || isExpandable);

  const content = (
    <div
      role={rowActs ? 'button' : undefined}
      tabIndex={rowActs ? 0 : undefined}
      aria-expanded={rowActs && isExpandable ? expanded : undefined}
      onKeyDown={
        rowActs
          ? (e) => {
              if (e.key !== 'Enter' && e.key !== ' ') return;
              if (e.target !== e.currentTarget) return;
              e.preventDefault();
              handleClick();
            }
          : undefined
      }
      className={cn(
        'flex items-start gap-3 rounded-lg px-2.5 py-2 transition-colors hover:bg-accent/40',
        !notification.isRead && 'bg-primary/5',
        (href || hasChatLink || isBackmix || isExpandable) && 'cursor-pointer',
        compact && 'px-2 py-2',
      )}
      onClick={handleClick}
    >
      <div className={cn('mt-0.5 shrink-0', config.color)}>
        <Icon className={cn('h-4 w-4', compact && 'h-3.5 w-3.5')} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className={cn(
            'text-sm font-medium',
            // See `showFullText`: an app name chopped mid-word is not enough
            // to decide a publish on. `min-w-0` on the flex child keeps the
            // wrapped title from pushing the timestamp out of the row.
            showFullText ? 'min-w-0 break-words' : 'truncate',
            !notification.isRead && 'text-foreground',
            notification.isRead && 'text-muted-foreground',
          )}>
            {notification.title}
          </span>
          <span className="text-[10px] text-muted-foreground/60 shrink-0">{timeAgo}</span>
          {isExpandable && (
            <ChevronRight
              className={cn(
                'h-3.5 w-3.5 ml-auto self-center shrink-0 text-muted-foreground/50 transition-transform',
                expanded && 'rotate-90',
              )}
            />
          )}
        </div>

        {bodyText && (
          <p className={cn(
            'text-xs mt-0.5',
            // Publish warnings read at full contrast: this is the text the
            // decision rests on, not a subtitle for a row in a list.
            showFullText ? 'text-foreground/80' : 'text-muted-foreground',
            !expanded && !showFullText && 'line-clamp-2',
            compact && !showFullText && 'line-clamp-1',
          )}>
            {bodyText}
          </p>
        )}

        {isExpandable && expanded && auditView && (
          <dl className="mt-2 space-y-1 rounded-lg border border-border/60 bg-muted/30 px-2.5 py-2 text-[11px]">
            {auditView.fields.map(({ label, value }) => (
              <div key={label} className="flex gap-2">
                <dt className="w-14 shrink-0 text-muted-foreground/70">{label}</dt>
                <dd className="min-w-0 break-words font-mono text-foreground/80">{value}</dd>
              </div>
            ))}
          </dl>
        )}

        {notification.actionTaken && isPayment && (
          <span className={cn(
            'inline-flex items-center gap-1 mt-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full',
            notification.actionTaken === 'approved' && 'bg-green-500/10 text-green-600',
            notification.actionTaken === 'denied' && 'bg-red-500/10 text-red-600',
            notification.actionTaken === 'expired' && 'bg-muted text-muted-foreground',
          )}>
            {notification.actionTaken === 'approved' && <><Check className="h-2.5 w-2.5" /> {t('notifications.approved')}</>}
            {notification.actionTaken === 'denied' && <><X className="h-2.5 w-2.5" /> {t('notifications.denied')}</>}
            {notification.actionTaken === 'expired' && t('notifications.expired')}
          </span>
        )}

        {data?.actions && Array.isArray(data.actions) && !notification.actionTaken && (
          // The labels are the sender's and can be long ("Raise its share to
          // 70%"). Each stays on ONE line and the row wraps: the buttons are
          // 24 px tall, and three of them sharing a 330 px sheet used to
          // break their labels over three lines, the last printed across
          // the title of the next notification (the visual pass of
          // 2026-10-03; two lines under the pill in the 420 px card).
          <div data-testid="notification-actions" className="flex flex-wrap items-center gap-1.5 mt-2">
            {(data.actions as Array<{ label: string; action: string; url?: string }>).map((cta, i) => (
              <button
                key={i}
                onClick={async (e) => {
                  e.stopPropagation();
                  setActionLoading(cta.action);
                  try {
                    const res = await fetch(`/api/notifications/${notification.id}`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ action: cta.action }),
                    });
                    if (res.ok) {
                      const result = await res.json();
                      onCtaAction?.(notification.id, cta.action);
                      if (result.redirectUrl) {
                        router.push(result.redirectUrl);
                      }
                    }
                  } finally {
                    setActionLoading(null);
                  }
                }}
                disabled={!!actionLoading}
                className="h-6 px-2.5 text-[11px] font-medium whitespace-nowrap rounded-md border border-border bg-background hover:bg-accent disabled:opacity-50 transition-colors"
              >
                {actionLoading === cta.action ? '...' : cta.label}
              </button>
            ))}
          </div>
        )}

        {data?.actions && notification.actionTaken && (
          // The chip names the button that was pressed, by its label, never
          // the action id (2026-10-09: "Let them continue" left a chip
          // reading "continue"); an id with no button left is "Done".
          <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
            <Check className="h-2.5 w-2.5" /> {(Array.isArray(data.actions) ? (data.actions as Array<{ label?: unknown; action?: unknown }>).find((a) => a && a.action === notification.actionTaken)?.label : null) as string | null | undefined || t('notifications.actionDone')}
          </span>
        )}

        {hasToolApprovalAction && (
          <div className="flex items-center gap-1.5 mt-2">
            <button
              onClick={(e) => { e.stopPropagation(); handleToolApproval('approve'); }}
              disabled={!!actionLoading}
              className="h-6 px-2.5 text-[11px] font-medium rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {actionLoading === 'approve' ? '...' : t('notifications.approve')}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); handleToolApproval('reject'); }}
              disabled={!!actionLoading}
              className="h-6 px-2.5 text-[11px] font-medium rounded-md bg-red-600/10 text-red-600 hover:bg-red-600/20 disabled:opacity-50 transition-colors"
            >
              {actionLoading === 'reject' ? '...' : t('notifications.deny')}
            </button>
          </div>
        )}

        {isToolApproval && notification.actionTaken && (
          <span className={cn(
            'inline-flex items-center gap-1 mt-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full',
            notification.actionTaken === 'approved' && 'bg-green-500/10 text-green-600',
            notification.actionTaken === 'rejected' && 'bg-red-500/10 text-red-600',
            notification.actionTaken === 'expired' && 'bg-muted text-muted-foreground',
          )}>
            {notification.actionTaken === 'approved' && <><Check className="h-2.5 w-2.5" /> {t('notifications.approved')}</>}
            {notification.actionTaken === 'rejected' && <><X className="h-2.5 w-2.5" /> {t('notifications.denied')}</>}
            {notification.actionTaken === 'expired' && t('notifications.expired')}
          </span>
        )}

        {hasPublishAction && (
          // Same PATCH as the CriticalToast: approve publishes the app as
          // this person and returns the listing, deny records the "no" and
          // publishes nothing. The label says the action, not "Allow" — the
          // person is agreeing to make an app public, not granting a
          // capability (2026-09-06).
          <div className="flex items-center gap-1.5 mt-2">
            <button
              onClick={(e) => { e.stopPropagation(); handlePermissionAction('approve'); }}
              disabled={!!actionLoading}
              className="h-6 px-2.5 text-[11px] font-medium rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {actionLoading === 'approve' ? '...' : t('criticalToast.publishApp')}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); handlePermissionAction('deny'); }}
              disabled={!!actionLoading}
              className="h-6 px-2.5 text-[11px] font-medium rounded-md bg-red-600/10 text-red-600 hover:bg-red-600/20 disabled:opacity-50 transition-colors"
            >
              {actionLoading === 'deny' ? '...' : t('criticalToast.dontPublish')}
            </button>
          </div>
        )}

        {isPublishRequest && notification.actionTaken && (() => {
          const taken = notification.actionTaken;
          const allowed = taken === 'approve' || taken === 'approved';
          const denied = taken === 'deny' || taken === 'denied';
          return (
            <span className={cn(
              'inline-flex items-center gap-1 mt-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full',
              allowed && 'bg-green-500/10 text-green-600',
              denied && 'bg-red-500/10 text-red-600',
              taken === 'expired' && 'bg-muted text-muted-foreground',
            )}>
              {allowed && <><Check className="h-2.5 w-2.5" /> {t('notifications.approved')}</>}
              {denied && <><X className="h-2.5 w-2.5" /> {t('notifications.denied')}</>}
              {taken === 'expired' && t('notifications.expired')}
            </span>
          );
        })()}

        {hasPermissionAction && (
          <div className="flex items-center gap-1.5 mt-2">
            <button
              onClick={(e) => { e.stopPropagation(); handlePermissionAction('approve'); }}
              disabled={!!actionLoading}
              className="h-6 px-2.5 text-[11px] font-medium rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {actionLoading === 'approve'
                ? '...'
                : `Allow${permissionFeature || permissionAgentName ? ` ${permissionFeature ?? permissionAgentName}` : ''}`}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); handlePermissionAction('deny'); }}
              disabled={!!actionLoading}
              className="h-6 px-2.5 text-[11px] font-medium rounded-md bg-red-600/10 text-red-600 hover:bg-red-600/20 disabled:opacity-50 transition-colors"
            >
              {actionLoading === 'deny' ? '...' : t('notifications.deny')}
            </button>
          </div>
        )}

        {isPermissionRequest && notification.actionTaken && (() => {
          // performCtaAction stores the raw action ('approve' / 'deny');
          // the server/realtime path may send past-tense — accept both.
          const taken = notification.actionTaken;
          const allowed = taken === 'approve' || taken === 'approved';
          const denied = taken === 'deny' || taken === 'denied';
          return (
            <span className={cn(
              'inline-flex items-center gap-1 mt-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full',
              allowed && 'bg-green-500/10 text-green-600',
              denied && 'bg-red-500/10 text-red-600',
            )}>
              {allowed && <><Check className="h-2.5 w-2.5" /> {t('notifications.approved')}</>}
              {denied && <><X className="h-2.5 w-2.5" /> {t('notifications.denied')}</>}
            </span>
          );
        })()}

        {hasAction && (
          // Same three-state picker as CriticalToast + ChatPaymentBanner.
          // The 'quick' / 'custom' sub-pickers swap into the same row
          // (no height growth) and use an inline back-arrow at the
          // left, not a trailing chip.
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            {picker === 'actions' && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); handleAction('approve_once'); }}
                  disabled={!!actionLoading}
                  className="h-6 px-2.5 text-[11px] font-medium rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  {actionLoading === 'approve_once' ? '...' : t('notifications.approve')}
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setPicker('quick'); }}
                  disabled={!!actionLoading}
                  className="h-6 px-2.5 text-[11px] font-medium rounded-md bg-primary/10 text-primary hover:bg-primary/20 disabled:opacity-50 transition-colors flex items-center gap-1"
                >
                  <ShieldCheck className="h-3 w-3" />
                  {t('notifications.setLimit')}
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleAction('deny'); }}
                  disabled={!!actionLoading}
                  className="h-6 px-2.5 text-[11px] font-medium rounded-md bg-red-600/10 text-red-600 hover:bg-red-600/20 disabled:opacity-50 transition-colors"
                >
                  {actionLoading === 'deny' ? '...' : t('notifications.deny')}
                </button>
              </>
            )}

            {picker === 'quick' && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); setPicker('actions'); }}
                  disabled={!!actionLoading}
                  aria-label={t('notifications.back')}
                  className="inline-flex items-center justify-center h-6 w-6 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent disabled:opacity-50"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleAction('always_approve', quick2x); }}
                  disabled={!!actionLoading || pickerBase <= 0}
                  className="h-6 px-2.5 text-[11px] font-medium rounded-md bg-primary/10 text-primary hover:bg-primary/20 disabled:opacity-50 transition-colors"
                  title={`Auto-approve up to ${fmtMoney(quick2x)} per request`}
                >
                  2x · {fmtMoney(quick2x)}
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleAction('always_approve', quick10x); }}
                  disabled={!!actionLoading || pickerBase <= 0}
                  className="h-6 px-2.5 text-[11px] font-medium rounded-md bg-primary/10 text-primary hover:bg-primary/20 disabled:opacity-50 transition-colors"
                  title={`Auto-approve up to ${fmtMoney(quick10x)} per request`}
                >
                  10x · {fmtMoney(quick10x)}
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setPicker('custom'); }}
                  disabled={!!actionLoading}
                  className="h-6 px-2.5 text-[11px] font-medium rounded-md bg-muted text-foreground hover:bg-accent disabled:opacity-50 transition-colors"
                >
                  {t('notifications.custom')}
                </button>
              </>
            )}

            {picker === 'custom' && (
              <form
                className="flex w-full items-center gap-1.5"
                onClick={(e) => e.stopPropagation()}
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const n = parseFloat(customStr);
                  if (!Number.isFinite(n) || n <= 0) return;
                  handleAction('always_approve', n);
                }}
              >
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setPicker('quick'); }}
                  disabled={!!actionLoading}
                  aria-label={t('notifications.back')}
                  className="inline-flex items-center justify-center h-6 w-6 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent disabled:opacity-50"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                </button>
                <span className="text-[11px] text-muted-foreground">
                  {t('notifications.upTo')}
                </span>
                <div className="inline-flex items-center rounded-md bg-muted px-1.5">
                  <input
                    ref={customInputRef}
                    type="number"
                    inputMode="decimal"
                    min={amountNum > 0 ? amountNum : 0.01}
                    step="0.01"
                    value={customStr}
                    onChange={(e) => setCustomStr(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        setPicker('quick');
                      }
                    }}
                    placeholder={
                      pickerBase > 0
                        ? pickerBase.toFixed(2)
                        : amountNum > 0
                          ? amountNum.toFixed(2)
                          : '0.10'
                    }
                    disabled={!!actionLoading}
                    className="bg-transparent px-0.5 py-0 h-5 text-[11px] w-16 outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <span className="text-[11px] text-muted-foreground">{t('settings.creditsUnit')}</span>
                </div>
                <button
                  type="submit"
                  disabled={
                    !!actionLoading ||
                    !Number.isFinite(parseFloat(customStr)) ||
                    parseFloat(customStr) <= 0
                  }
                  className="ml-auto h-6 px-2.5 text-[11px] font-medium rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  {actionLoading === 'always_approve' ? '...' : t('notifications.apply')}
                </button>
              </form>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col items-center gap-1 mt-1 shrink-0">
        {!notification.isRead && (
          <div className="h-2 w-2 rounded-full bg-primary" />
        )}
        {!compact && onMute && notification.sourceType && notification.sourceId && notification.type !== 'payment_required' && (
          <Tip label={t('notifications.muteSource')}>
            <button
              type="button"
              aria-label={t('notifications.muteSource')}
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); onMute(notification.sourceType!, notification.sourceId!); }}
              className="h-5 w-5 flex items-center justify-center rounded text-muted-foreground/40 hover:text-muted-foreground transition-colors"
            >
              <BellOff className="h-3 w-3" />
            </button>
          </Tip>
        )}
      </div>
    </div>
  );

  const backmixDialog = isBackmix ? (
    <BackmixDialog
      open={backmixOpen}
      onClose={() => setBackmixOpen(false)}
      mode="accept"
      appName={(data?.targetTitle as string) || 'your app'}
      proposal={{
        proposalPostId: String(data?.proposalPostId ?? ''),
        forkName: (data?.forkName as string) || 'a remix',
        message: (data?.message as string) ?? null,
        markerIds: Array.isArray(data?.markerIds) ? (data.markerIds as string[]) : [],
        discussionPostId: data?.proposalPostId ? String(data.proposalPostId) : undefined,
      }}
    />
  ) : null;

  if (href) {
    /* A link that carries a fragment is a FULL page load, never a client
       navigation. An app's notice points at the app's entry link
       (`/?app=revent#inbox=a:...`), which the server answers with a redirect
       to the person's workspace. `next/link` follows that redirect inside the
       client router and lands on the redirect's address WITHOUT the fragment,
       so Revent opened on its front page instead of on the item (host-claim
       pass, 2026-10-01, finding 4: the same address typed into a tab worked).
       Only a document navigation makes the browser re-attach the fragment
       across a redirect, and that is the arrival lib/workspaces/
       fragment-target.ts hands to the one app the link names. The row's
       mark-read request is sent keepalive (hooks/useNotifications.ts), so
       leaving the page does not cancel it. */
    if (href.includes('#')) {
      return <a href={href} className="block" data-full-navigation="">{content}</a>;
    }
    return <Link href={href} className="block">{content}</Link>;
  }
  return (
    <>
      {content}
      {backmixDialog}
    </>
  );
}

function getTimeAgo(date: Date | string, t: (key: string, params?: Record<string, any>) => string): string {
  const now = Date.now();
  const then = new Date(date).getTime();
  const diff = now - then;

  if (diff < 60_000) return t('time.now');
  if (diff < 3600_000) return t('time.minutesShort', { count: Math.floor(diff / 60_000) });
  if (diff < 86400_000) return t('time.hoursShort', { count: Math.floor(diff / 3600_000) });
  if (diff < 604800_000) return t('time.daysShort', { count: Math.floor(diff / 86400_000) });
  return new Date(date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
